import { useState } from 'react';
import { Sparkles, Landmark, ScrollText, UtensilsCrossed, ConciergeBell, Shirt, Wine, Phone, ChevronDown } from 'lucide-react';
import { DATA, money, LANGS, FLAGS, SECTIONS } from '@/data/guideData';
import { I18N } from '@/data/guideTranslations';

const SECTION_ICONS = {
  welcome: Sparkles, history: Landmark, rules: ScrollText, roomservice: UtensilsCrossed,
  services: ConciergeBell, laundry: Shirt, minibar: Wine, wines: Wine, ext: Phone
};

function fill(str, vars) {
  return str.replace(/\{(\w+)\}/g, (m, k) => (vars[k] !== undefined ? vars[k] : m));
}

const PriceRow = ({ label, value, dashLabel }) => {
  const v = money(value);
  return (
    <div className="flex items-center justify-between gap-4 py-2 border-b border-gray-100 last:border-0" data-testid="guide-price-row">
      <span className="text-gray-700 text-sm">{label}</span>
      <span className="text-slate-900 font-bold text-sm whitespace-nowrap">{v === null ? (dashLabel || '—') : v}</span>
    </div>
  );
};

const PriceTable = ({ colItem, cols, rows }) => (
  <div className="overflow-x-auto -mx-2 sm:mx-0">
    <table className="w-full text-sm" data-testid="guide-laundry-table">
      <thead>
        <tr className="border-b-2 border-[#C5A059]/40">
          <th className="text-left py-2 px-2 text-slate-900 font-bold">{colItem}</th>
          {cols.map((c) => (
            <th key={c.key} className="text-right py-2 px-2 text-slate-900 font-bold">{c.label}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-b border-gray-100">
            <td className="py-2 px-2 text-gray-700">{r.item}</td>
            {cols.map((c) => {
              const v = r[c.key];
              return (
                <td key={c.key} className="py-2 px-2 text-right text-slate-900 font-semibold">
                  {v === null || v === undefined ? <span className="text-gray-400">—</span> : money(v)}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const Card = ({ children, className = '' }) => (
  <div className={`bg-white rounded-2xl shadow-xl p-6 sm:p-8 ${className}`} data-testid="guide-card">{children}</div>
);

const SectionTitle = ({ children }) => (
  <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mb-6" data-testid="guide-section-title">{children}</h2>
);

const SubHead = ({ children }) => (
  <div className="text-[#C5A059] font-bold uppercase tracking-wide text-xs mb-3">{children}</div>
);

const GuiaHospede = () => {
  const [lang, setLang] = useState(() => {
    try {
      const saved = localStorage.getItem('mondego_guide_lang');
      if (saved && LANGS.includes(saved)) return saved;
    } catch (e) { /* noop */ }
    return 'pt';
  });
  const [section, setSection] = useState('welcome');
  const [langMenuOpen, setLangMenuOpen] = useState(false);

  const t = I18N[lang];

  const changeLang = (l) => {
    setLang(l);
    setLangMenuOpen(false);
    try { localStorage.setItem('mondego_guide_lang', l); } catch (e) { /* noop */ }
  };

  const renderSection = () => {
    if (section === 'welcome') {
      const w = t.welcome;
      return (
        <Card>
          <SectionTitle>{w.title}</SectionTitle>
          <div className="space-y-4">
            {w.body.map((p, i) => <p key={i} className="text-gray-600 leading-relaxed">{p}</p>)}
            <p className="text-slate-900 font-semibold italic pt-2">{w.signature}</p>
          </div>
        </Card>
      );
    }

    if (section === 'history') {
      const h = t.history;
      return (
        <Card>
          <SectionTitle>{h.title}</SectionTitle>
          <div className="space-y-4">
            {h.body.map((p, i) => <p key={i} className="text-gray-600 leading-relaxed">{p}</p>)}
          </div>
        </Card>
      );
    }

    if (section === 'rules') {
      const r = t.rules;
      const vars = DATA.hours;
      return (
        <Card>
          <SectionTitle>{r.title}</SectionTitle>
          <ol className="space-y-4">
            {r.items.map((item, i) => (
              <li key={i} className="flex gap-3" data-testid={`guide-rule-item-${i}`}>
                <span className="flex-shrink-0 w-7 h-7 rounded-full bg-[#C5A059]/10 text-[#C5A059] font-bold text-sm flex items-center justify-center">{i + 1}</span>
                <span className="text-gray-600 leading-relaxed pt-0.5">{fill(item, vars)}</span>
              </li>
            ))}
          </ol>
        </Card>
      );
    }

    if (section === 'roomservice') {
      const rs = t.roomservice;
      const vars = { start: DATA.hours.roomServiceStart, end: DATA.hours.roomServiceEnd,
        breakfastStart: DATA.hours.breakfastStart, breakfastEnd: DATA.hours.breakfastEnd };
      return (
        <Card>
          <SectionTitle>{rs.title}</SectionTitle>
          <p className="text-gray-600 leading-relaxed mb-2">{fill(rs.intro, vars)}</p>
          <p className="text-gray-600 leading-relaxed mb-6">{fill(rs.dial, vars)}</p>

          <div className="mb-6 pb-6 border-b border-gray-100">
            <SubHead>{rs.breakfastTitle}</SubHead>
            <p className="text-gray-600 text-sm leading-relaxed mb-3">{rs.breakfastDesc}</p>
            <PriceRow label={rs.breakfastTitle} value={DATA.roomService.breakfast} />
            <p className="text-xs text-gray-400 italic mt-2">{fill(rs.breakfastNote, vars)}</p>
          </div>

          <div className="mb-6 pb-6 border-b border-gray-100">
            <SubHead>{rs.teaTitle}</SubHead>
            <PriceRow label={rs.teaSimpleLabel} value={DATA.roomService.teaSimple} />
            <PriceRow label={rs.cheeseBreadLabel} value={DATA.roomService.cheeseBread} />
          </div>

          <div>
            <SubHead>{rs.drinksTitle}</SubHead>
            <PriceRow label={rs.hotBeverageLabel} value={DATA.roomService.hotBeverageJug} />
            <PriceRow label={rs.freshJuiceLabel} value={DATA.roomService.freshJuice} />
          </div>
        </Card>
      );
    }

    if (section === 'services') {
      const s = t.services;
      const vars = DATA.hours;
      const blocks = [s.events, s.safe, { ...s.laundryS, body: fill(s.laundryS.body, vars) }, s.help];
      return (
        <Card>
          <SectionTitle>{s.title}</SectionTitle>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {blocks.map((b, i) => (
              <div key={i}>
                <SubHead>{b.title}</SubHead>
                <p className="text-gray-600 text-sm leading-relaxed">{b.body}</p>
              </div>
            ))}
          </div>
        </Card>
      );
    }

    if (section === 'laundry') {
      const l = t.laundry;
      const vars = DATA.hours;
      const rows = DATA.laundry.map((row) => ({ item: l.names[row.key], washIron: row.wash, iron: row.iron }));
      return (
        <Card>
          <SectionTitle>{l.title}</SectionTitle>
          <p className="text-gray-600 text-sm leading-relaxed mb-2">{fill(l.hoursNote, vars)}</p>
          <p className="text-gray-600 text-sm leading-relaxed mb-6">{fill(l.urgentNote, vars)} {fill(l.pressOnlyNote, vars)}</p>
          <PriceTable colItem={l.colItem} cols={[{ key: 'washIron', label: l.colWashIron }, { key: 'iron', label: l.colIron }]} rows={rows} />
        </Card>
      );
    }

    if (section === 'minibar') {
      const m = t.minibar;
      return (
        <div className="space-y-6">
          <Card>
            <SectionTitle>{m.title}</SectionTitle>
            <div className="mb-6 pb-6 border-b border-gray-100">
              <SubHead>{m.drinksTitle}</SubHead>
              {DATA.minibarDrinks.map((d) => <PriceRow key={d.key} label={m.names[d.key]} value={d.price} />)}
            </div>
            <div className="mb-6 pb-6 border-b border-gray-100">
              <SubHead>{m.alcoholTitle}</SubHead>
              {DATA.minibarAlcohol.map((d) => <PriceRow key={d.key} label={m.names[d.key]} value={d.price} />)}
            </div>
            <div>
              <SubHead>{m.wineTitle}</SubHead>
              {DATA.minibarWine.map((d) => <PriceRow key={d.key} label={m.names[d.key]} value={d.price} />)}
            </div>
          </Card>
          <Card>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <SubHead>{m.sparklingTitle}</SubHead>
                {DATA.sparkling.map((d) => <PriceRow key={d.key} label={m.names[d.key]} value={d.price} />)}
              </div>
              <div>
                <SubHead>{m.beerTitle}</SubHead>
                {DATA.beers.map((d) => <PriceRow key={d.key} label={m.names[d.key]} value={d.price} />)}
              </div>
            </div>
          </Card>
        </div>
      );
    }

    if (section === 'wines') {
      const w = t.wines;
      return (
        <Card>
          <SectionTitle>{w.title}</SectionTitle>
          <div className="mb-6 pb-6 border-b border-gray-100">
            <SubHead>{w.whiteTitle}</SubHead>
            {DATA.wineWhite.map((d) => <PriceRow key={d.key} label={w.names[d.key]} value={d.price} />)}
          </div>
          <div className="mb-6 pb-6 border-b border-gray-100">
            <SubHead>{w.redTitle}</SubHead>
            {DATA.wineRed.map((d) => <PriceRow key={d.key} label={w.names[d.key]} value={d.price} />)}
          </div>
          <PriceRow label={w.corkageLabel} value={DATA.corkage} />
        </Card>
      );
    }

    if (section === 'ext') {
      const x = t.ext;
      return (
        <Card>
          <SectionTitle>{x.title}</SectionTitle>
          <p className="text-gray-600 text-sm leading-relaxed mb-6">{x.howTo}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <SubHead>{x.roomLabel}</SubHead>
              <div className="grid grid-cols-3 gap-2">
                {DATA.extensionsRooms.map((r) => (
                  <div key={r} className="text-center py-2 rounded-lg bg-gray-50 text-slate-900 font-semibold text-sm" data-testid={`guide-room-ext-${r}`}>{r}</div>
                ))}
              </div>
            </div>
            <div>
              <SubHead>{t.services.title}</SubHead>
              {DATA.extensionsCommon.map((c) => (
                <PriceRow key={c.key} label={x.names[c.key]} value={undefined} dashLabel={c.ext.join(` ${t.or} `)} />
              ))}
            </div>
          </div>
        </Card>
      );
    }

    return null;
  };

  return (
    <div className="min-h-screen bg-[#f6f6f8]" data-testid="guia-hospede-page">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <img
              src="https://customer-assets.emergentagent.com/job_preview-site-81/artifacts/wtv4k322_fechadura.png"
              alt="Pousada do Mondego"
              className="w-8 h-11 flex-shrink-0"
            />
            <div className="min-w-0">
            <h1 className="text-lg font-bold text-slate-900 uppercase tracking-tight truncate">Pousada do Mondego</h1>
              <p className="text-[10px] uppercase tracking-widest text-[#C5A059] font-semibold">{t.dirLabel}</p>
            </div>
          </div>

          <div className="relative flex-shrink-0">
            <button
              type="button"
              onClick={() => setLangMenuOpen((o) => !o)}
              className="flex items-center gap-2 border border-gray-200 rounded-full px-3 py-2 text-sm font-semibold text-slate-900 hover:border-[#C5A059] transition-colors"
              data-testid="guide-lang-toggle"
              aria-haspopup="true"
              aria-expanded={langMenuOpen}
            >
              <span>{FLAGS[lang]}</span>
              <span>{lang.toUpperCase()}</span>
              <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform ${langMenuOpen ? 'rotate-180' : ''}`} />
            </button>
            {langMenuOpen && (
              <div className="absolute right-0 top-full mt-2 bg-white border border-gray-200 rounded-xl shadow-xl py-1 min-w-[160px] z-50" data-testid="guide-lang-menu">
                {LANGS.map((l) => (
                  <button
                    key={l}
                    type="button"
                    onClick={() => changeLang(l)}
                    className={`w-full flex items-center gap-2 px-4 py-2 text-sm text-left hover:bg-gray-50 ${l === lang ? 'text-[#C5A059] font-bold' : 'text-slate-900'}`}
                    data-testid={`guide-lang-option-${l}`}
                  >
                    <span>{FLAGS[l]}</span>
                    <span>{I18N[l].langName}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Section tabs */}
        <div className="max-w-4xl mx-auto px-4 sm:px-6 pb-3 overflow-x-auto">
          <div className="flex gap-2 min-w-max" data-testid="guide-section-nav">
            {SECTIONS.map((s) => {
              const Icon = SECTION_ICONS[s];
              const active = s === section;
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => setSection(s)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${
                    active ? 'bg-[#C5A059] text-[#101B2D]' : 'bg-gray-100 text-slate-600 hover:bg-gray-200'
                  }`}
                  data-testid={`guide-tab-${s}`}
                  aria-current={active}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {t.nav[s]}
                </button>
              );
            })}
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-10" data-testid="guide-content">
        {renderSection()}
      </main>

      {/* Footer */}
      <footer className="bg-[#101B2D] text-white/80 py-10" data-testid="guide-footer">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 text-sm">
            <div>
              <div className="text-[#C5A059] font-bold uppercase tracking-wide text-xs mb-2">{t.footer.addressLabel}</div>
              <div>{DATA.contact.address}</div>
            </div>
            <div>
              <div className="text-[#C5A059] font-bold uppercase tracking-wide text-xs mb-2">{t.footer.phoneLabel} / {t.footer.whatsappLabel}</div>
              <div><a href={`tel:${DATA.contact.phone.replace(/[^\d+]/g, '')}`} className="hover:text-[#C5A059]">{DATA.contact.phone}</a></div>
              <div><a href={`https://wa.me/${DATA.contact.whatsapp.replace(/[^\d]/g, '')}`} className="hover:text-[#C5A059]">{DATA.contact.whatsapp}</a></div>
              <div><a href={`mailto:${DATA.contact.email}`} className="hover:text-[#C5A059]">{DATA.contact.email}</a></div>
            </div>
          </div>
          <p className="text-xs text-white/50 mt-6">{t.footer.note}</p>
        </div>
      </footer>
    </div>
  );
};

export default GuiaHospede;
